// Vendor Libs
import React from 'react';
import PropTypes from 'prop-types';
import {get, map, pick, startCase, toString} from 'lodash';

// Components
import AccordionButton from '../core/accordion-button';
import AccordionPanel from '../core/accordion-panel';
import BingeableIndicator from './bingeable-indicator';
import Icon from '../core/icon';
import MatchConfidenceReason from './match-confidence-reason';
import RecordDetails from '../core/record-details';
import TooltipTrigger from '../core/tooltip-trigger';
import MappingButton from './mapping-button';
import FlaggedMetadataIndicator from './flagged-metadata-indicator';

// Core Libs
import {formatDate, formatDuration} from '../../lib/core/date';

// Config
import echostarContentTypeMap from '../../bootstrap-data/echostar-content-type-map';
import recordDetailsConfig from '../../../config/record-details-config';

// Styles
import './best-matches-item.scss';
import {ColoredRow, getRowsColorLogic} from '../core/colorizeRows';

function BestMatchesItem({isEpisodic, massShellActive, record, renderThumbnails}) {
    const getAccordionProps = (details, initialDetailsOrder) => ({
        disableDefaultToggle: true,
        detailsRenderer: () => renderRecordDetails(details, initialDetailsOrder),
    });

    const getContentTypeName = (echostarContentType) =>
        get(echostarContentTypeMap, `${echostarContentType}.name`) || 'Unknown';

    const getCountContent = (episodeCount, mappedCounts) => {
        const {DISH, SLING} = mappedCounts;
        let countContent = null;

        if (episodeCount || DISH || 0 + SLING || 0) {
            const countText = [];

            if (episodeCount) {
                countText.push(`Episodes: ${episodeCount}`);
            }

            if (DISH || SLING) {
                countText.push(`Mapped: D: ${DISH || 0}, S: ${SLING || 0}`);
            }

            countContent = <span className="best-matches-item--count">({countText.join(', ')})</span>;
        }

        return countContent;
    };

    const getTooltipProps = () => ({
        className: 'best-matches-item--confidence',
        tooltipRenderer: renderMatchReason,
    });

    const renderButton = (item) => {
        const buttonProps = {
            ...pick(item, [
                'description',
                'descriptionLanguage',
                'echostarContentType',
                'echostarId',
                'episodeCount',
                'episodeNumber',
                'episodeSeason',
                'genres',
                'gnSeriesId',
                'originalAirDate',
                'rating',
                'releaseYear',
                'roviProgramId',
                'runtime',
                'seriesId',
                'seriesTitle',
                'shortDescription',
                'title',
                'titleLanguages',
                'tmsId',
                'sourceId',
            ]),
        };

        return (
            <MappingButton
                {...buttonProps}
                className="best-matches-item--map-button"
                disabled={massShellActive}
                isShell={!item.roviProgramId && !item.tmsId && !item.sourceId}
                servicesDataKey="bestMatches"
            />
        );
    };

    const renderDetailItemContent = (detail, key) => {
        const {type, format, parseFormat} = get(recordDetailsConfig, key, {});
        const formats = {
            contentType: () => getContentTypeName(detail),
            date: () => formatDate(detail, format, parseFormat),
        };

        return formats[type] ? formats[type]() : toString(detail);
    };

    const renderDetails = (details) => {
        const detailOrder = [
            'seriesTitle',
            'title',
            'description',
            'episodeSeason',
            'episodeNumber',
            'releaseYear',
            'runtime',
            'originalAirDate',
            isEpisodic(details) ? 'gnSeriesId' : undefined,
            isEpisodic(details) ? 'seriesId' : undefined,
            'echostarId',
        ];

        return (
            <AccordionPanel {...getAccordionProps(details, detailOrder)}>
                <dl className="best-matches-item--list">
                    {map(detailOrder, (key) => {
                        const value = key === 'runtime' ? formatDuration(details[key]) : details[key];

                        if (value !== null) {
                            return [
                                <dt className="best-matches-item--term" key={`${key}Term`}>
                                    {startCase(key)}
                                </dt>,
                                <dd className="best-matches-item--description" key={`${key}Desc`}>
                                    {renderDetailItemContent(value, key)}
                                </dd>,
                            ];
                        }
                    })}
                    <dt className="best-matches-item--term">
                        <AccordionButton>More Details</AccordionButton>
                    </dt>
                </dl>
            </AccordionPanel>
        );
    };

    const renderMatchReason = () => <MatchConfidenceReason items={record.confidenceReason} />;

    const renderMatchesTitle = (
        echostarContentType,
        containsRoviId,
        containsTmsId,
        containsSourceId,
        episodeCount,
        mappedCounts = {},
        flaggedMetadata,
        vfb
    ) => {
        const shell =
            containsRoviId || containsTmsId || containsSourceId ? null : (
                <span className="best-matches-item--shell">
                    <Icon
                        className="best-matches-item--shell-icon"
                        fontType="mc"
                        iconName="conch-shell"
                        title="AMS shell"
                    />
                    Shell
                </span>
            );
        const bingeable = <BingeableIndicator vfb={vfb} />;
        const countContent = getCountContent(episodeCount, mappedCounts);
        const flagged = <FlaggedMetadataIndicator flaggedMetadata={flaggedMetadata} />;

        return (
            <div className="best-matches-item--title">
                Suggested {shell} {bingeable} {getContentTypeName(echostarContentType)} Detail {countContent} {flagged}
            </div>
        );
    };

    const renderRecordDetails = (details, blacklistKeys) => (
        <RecordDetails
            blacklistKeys={blacklistKeys}
            className="best-matches-item--records"
            keyOrder="moviesAndPrograms"
            searchData={[details]}
        />
    );

    const renderRows = () => (
        <div className="best-matches-item" key={item.key}>
            <div className="best-matches-item--header">
                {renderMatchesTitle(
                    item.echostarContentType,
                    !!item.roviProgramId,
                    !!item.tmsId,
                    !!item.sourceId,
                    item.episodeCount,
                    item.mappedCounts,
                    item.flaggedMetadata,
                    item.vfb
                )}
                <div className="best-matches-item--header-actions">
                    <TooltipTrigger {...getTooltipProps()}>{record.confidence}% match</TooltipTrigger>
                    {renderButton(item)}
                </div>
            </div>
            {renderThumbnails(item)}
            <div className="best-matches-item--details">{renderDetails(item)}</div>
        </div>
    );

    const item = record ? record.result : null;
    const {rowOptions} = getRowsColorLogic(item, true, '');
    return item ? <ColoredRow rowOptions={rowOptions}>{renderRows()}</ColoredRow> : null;
}

BestMatchesItem.propTypes = {
    isEpisodic: PropTypes.func.isRequired,
    massShellActive: PropTypes.bool,
    record: PropTypes.object.isRequired,
    renderThumbnails: PropTypes.func.isRequired,
};

export default BestMatchesItem;
